import React, { ReactNode, createContext, useContext, useState } from "react";
import cn from "classnames";
import s from "./ComparisonTable.module.scss";
import c from "../../styles/container.module.scss";
import bs from "../../styles/block.spacing.module.scss";
import BlockIntro from "../Common/BlockIntro";
import BlockBackground from "../Common/BlockBackground";
import SliderButton from "../Common/SliderButton/SliderButton";
import {
  InnerSpacingVariant,
  OuterSpacingVariant,
  StyleVariant,
} from "../../lib/storyblok/types/shared";
import type { RichTextContent } from "../../lib/storyblok/types/fieldtypes/richText";
import Header from "./components/Header";
import HeaderColumn from "./components/HeaderColumn";
import Row from "./components/Row";
import RowColumn from "./components/RowColumn";
import Section from "./components/Section";
import Navigation from "./components/Navigation";
import { useScrollManager } from "./hooks/useScrollManager";
import { useColumnWidths } from "./hooks/useColumnWidths";
import { useTouchGestures } from "./hooks/useTouchGestures";

export const HighlightedColumnContext = createContext<number | undefined>(
  undefined
);
export const useHighlightedColumn = () => useContext(HighlightedColumnContext);

export const SectionContext = createContext<{
  expandedSections: Set<string>;
  toggleSection: (sectionId: string) => void;
  hasMultipleSections: boolean;
}>({
  expandedSections: new Set(),
  toggleSection: () => {},
  hasMultipleSections: false,
});
export const useSectionContext = () => useContext(SectionContext);

type ProcessedSection = {
  id: string;
  component: "comparisonTableSection";
  title?: string;
  icon?: string;
  rows: ReactNode[];
  _uid: string;
};

type ProcessedData = {
  sections: ProcessedSection[];
  hasMultipleSections: boolean;
};

const subComponents = {
  Header,
  HeaderColumn,
  Row,
  RowColumn,
  Section,
  Navigation,
};
type SubComponents = typeof subComponents;

type Props = {
  title?: string;
  description?: RichTextContent;
  buttons?: ReactNode;
  style: StyleVariant;
  innerSpacing: InnerSpacingVariant;
  outerSpacing: OuterSpacingVariant;
  inverted?: boolean;
  highlightedStyle: "black" | "blue" | "signature" | "peach" | "violet";
  highlightedColumn?: number;
  children?: ReactNode;
};

const processRowsIntoSections = (children: ReactNode): ProcessedData => {
  const sections: ProcessedSection[] = [];
  let activeSection: ProcessedSection | null = null;

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === Header) {
        return;
      }

      if (child.type === Section) {
        if (activeSection) {
          sections.push(activeSection);
        }

        activeSection = {
          id: child.props._uid || `section-${sections.length}`,
          component: "comparisonTableSection",
          title: child.props.title,
          icon: child.props.icon,
          rows: [],
          _uid: child.props._uid || `section-${sections.length}`,
        };
      } else {
        if (!activeSection) {
          activeSection = {
            id: "default-section",
            component: "comparisonTableSection",
            title: "",
            icon: "",
            rows: [],
            _uid: "default-section",
          };
        }
        activeSection.rows.push(child);
      }
    }
  });

  if (activeSection) {
    sections.push(activeSection);
  }

  return {
    sections,
    hasMultipleSections: sections.length > 1,
  };
};

const ComparisonTable: React.FC<Props> & SubComponents = ({
  title,
  description,
  buttons,
  style,
  outerSpacing,
  innerSpacing,
  inverted,
  highlightedColumn,
  highlightedStyle,
  children,
}) => {
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLDivElement>(null);

  const processedData = React.useMemo(
    () => processRowsIntoSections(children),
    [children]
  );

  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    if (
      processedData.hasMultipleSections &&
      processedData.sections.length > 0
    ) {
      return new Set([processedData.sections[0].id]);
    }
    return new Set();
  });

  const toggleSection = React.useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  }, []);

  const headerComponent = React.useMemo(
    () =>
      React.Children.toArray(children).find(
        (child) => React.isValidElement(child) && child.type === Header
      ),
    [children]
  );

  const dataColumnCount = React.useMemo(() => {
    if (React.isValidElement(headerComponent)) {
      const headerChildren = React.Children.toArray(
        headerComponent.props.children
      );
      return headerChildren.length;
    }
    return 0;
  }, [headerComponent]);

  const columnWidthConfig = useColumnWidths(
    tableRef,
    tableContainerRef,
    dataColumnCount
  );

  const scrollManager = useScrollManager(tableContainerRef, {
    dataWidth: columnWidthConfig.dataWidth,
    visibleColumns: columnWidthConfig.visibleColumns,
    scrollableColumns: columnWidthConfig.scrollableColumns,
    featureWidth: columnWidthConfig.featureWidth,
    shouldScroll: columnWidthConfig.shouldScroll,
  });

  // Add touch gesture support for mobile navigation
  useTouchGestures(
    tableContainerRef,
    scrollManager.scrollRight,
    scrollManager.scrollLeft
  );

  // Memoize context value separately to prevent unnecessary re-renders
  const sectionContextValue = React.useMemo(
    () => ({
      expandedSections,
      toggleSection,
      hasMultipleSections: processedData.hasMultipleSections,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expandedSections, processedData.hasMultipleSections]
    // toggleSection is excluded as it's stable with useCallback
  );

  const renderTableContent = React.useCallback(() => {
    if (!processedData.hasMultipleSections) {
      const nonHeaderChildren = React.Children.toArray(children).filter(
        (child) =>
          !(React.isValidElement(child) && child.type === Header) &&
          !(React.isValidElement(child) && child.type === Section)
      );

      return nonHeaderChildren;
    }

    const sectionsContent = processedData.sections.map((section) => {
      const isExpanded = expandedSections.has(section.id);
      const showSection = section.title || section.icon;

      return (
        <React.Fragment key={section.id}>
          {showSection && (
            <Section
              title={section.title}
              isExpanded={isExpanded}
              onToggle={() => toggleSection(section.id)}
              hasMultipleSections={processedData.hasMultipleSections}
              columnCount={dataColumnCount}
            />
          )}
          <div
            className={cn(s.sectionContent, {
              [s.expanded]: isExpanded,
            })}
          >
            {section.rows}
          </div>
        </React.Fragment>
      );
    });

    return sectionsContent;
  }, [
    processedData,
    expandedSections,
    toggleSection,
    dataColumnCount,
    children,
  ]);

  // Screen reader announcement ref
  const announcementRef = React.useRef<HTMLDivElement>(null);

  // Announce scroll state changes for screen readers
  React.useEffect(() => {
    if (
      announcementRef.current &&
      scrollManager.isScrolled &&
      columnWidthConfig.shouldScroll
    ) {
      const currentCol = scrollManager.currentColumnIndex + 1;
      const visibleCols = columnWidthConfig.visibleColumns;
      const totalCols = dataColumnCount;

      announcementRef.current.textContent = `Showing columns ${currentCol} to ${Math.min(
        currentCol + visibleCols - 1,
        totalCols
      )} of ${totalCols}`;
    }
  }, [
    scrollManager.currentColumnIndex,
    columnWidthConfig.visibleColumns,
    dataColumnCount,
    scrollManager.isScrolled,
    columnWidthConfig.shouldScroll,
  ]);

  return (
    <HighlightedColumnContext.Provider value={highlightedColumn}>
      <SectionContext.Provider value={sectionContextValue}>
        <section
          className={cn(s.comparisonTable, bs[innerSpacing], bs[outerSpacing])}
          aria-label={title || "Product comparison table"}
        >
          <BlockBackground style={style} opacity={100} />

          {/* Screen reader announcements */}
          <div
            ref={announcementRef}
            className="sr-only"
            aria-live="polite"
            aria-atomic="true"
          />

          <div className={cn(dataColumnCount <= 2 ? c.wide : c.xWide, s.inner)}>
            <BlockIntro
              alignment="alignCenter"
              title={title}
              description={description}
              buttons={buttons}
              inverted={inverted}
              width="contained"
              spaceHorizontal={false}
            />

            <div
              className={cn(s.tableWrapper, {
                [s[highlightedStyle]]: highlightedStyle,
              })}
            >
              <div
                ref={tableContainerRef}
                className={cn(s.tableContainer, {
                  [s.scrolled]: scrollManager.isScrolled,
                  [s.canScrollRight]: scrollManager.canScrollRight,
                  [s.canScrollLeft]: scrollManager.canScrollLeft,
                })}
                role="region"
                aria-label="Scrollable table content"
              >
                <div
                  ref={tableRef}
                  className={s.table}
                  role="table"
                  aria-label="Product comparison"
                  aria-describedby="table-instructions"
                >
                  {headerComponent}
                  {renderTableContent()}
                </div>
              </div>
            </div>

            <Navigation
              showPrev={scrollManager.hasScrollableContent}
              showNext={scrollManager.hasScrollableContent}
            >
              {scrollManager.hasScrollableContent && (
                <>
                  <div className={s.navigationButton}>
                    <SliderButton
                      onClick={scrollManager.scrollLeft}
                      align="prev"
                      label="Previous"
                      inverted={inverted}
                      disabled={!scrollManager.canScrollLeft}
                    />
                  </div>
                  <div className={s.navigationButton}>
                    <SliderButton
                      onClick={scrollManager.scrollRight}
                      align="next"
                      label="Next"
                      inverted={inverted}
                      disabled={!scrollManager.canScrollRight}
                    />
                  </div>
                </>
              )}
            </Navigation>
          </div>
        </section>
      </SectionContext.Provider>
    </HighlightedColumnContext.Provider>
  );
};

ComparisonTable.Header = Header;
ComparisonTable.HeaderColumn = HeaderColumn;
ComparisonTable.Row = Row;
ComparisonTable.RowColumn = RowColumn;
ComparisonTable.Section = Section;
ComparisonTable.Navigation = Navigation;

export default ComparisonTable;
